/**
 * «Идея или проблема» — прямая линия к тем, кто делает приложение.
 *
 * Форма намеренно короткая: одна кнопка вида, одно поле и отправка. Чем
 * больше полей, тем меньше людей дописывают до конца, а на закрытой бете
 * важна каждая реплика.
 */

import { useState } from 'react'
import { useLocation } from 'react-router-dom'

import {
  FEEDBACK_ICONS,
  FEEDBACK_LABELS,
  FEEDBACK_PLACEHOLDERS,
  useSendFeedback,
  type FeedbackKind,
} from '@/shared/api/feedback'
import { haptic } from '@/shared/telegram'
import { Button } from '@/shared/ui/Button'
import { Icon } from '@/shared/ui/Icon'
import { Sheet } from '@/shared/ui/Sheet'
import { TextArea } from '@/shared/ui/fields'
import { ErrorBanner } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

const KINDS: FeedbackKind[] = ['idea', 'problem', 'praise']
const MIN_LEN = 5
const MAX_LEN = 2000

export function FeedbackSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const location = useLocation()
  const [kind, setKind] = useState<FeedbackKind>('idea')
  const [text, setText] = useState('')

  const reset = () => {
    setKind('idea')
    setText('')
  }

  const send = useSendFeedback({
    onSuccess: () => {
      haptic('success')
      toast.success('Спасибо, мы прочитаем')
      reset()
      onClose()
    },
    onError: (error) => toast.error(error.message),
  })

  const close = () => {
    reset()
    onClose()
  }

  return (
    <Sheet
      open={open}
      onClose={close}
      title="Идея или проблема"
      footer={
        <Button
          block
          size="lg"
          loading={send.isPending}
          disabled={text.trim().length < MIN_LEN}
          onClick={() =>
            send.mutate({
              kind,
              text: text.trim(),
              // экран нужен, чтобы не переспрашивать «а где именно?»
              screen: location.pathname.replace('/', '') || 'today',
            })
          }
        >
          Отправить
        </Button>
      }
    >
      {send.error && <ErrorBanner message={send.error.message} />}

      <p className="text-text-muted text-note leading-snug">
        ЯНяня ещё растёт, и мы читаем каждое сообщение. Ответим, если нужно.
      </p>

      <div className="mt-4 flex gap-1">
        {KINDS.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => {
              haptic('select')
              setKind(item)
            }}
            className={
              'press min-h-[44px] flex-1 rounded-[var(--radius-button)] px-2 text-note ' +
              'font-medium transition-colors ' +
              (kind === item ? 'bg-accent-soft text-accent' : 'bg-surface-muted text-text-muted')
            }
          >
            <Icon name={FEEDBACK_ICONS[item]} size={17} className="mr-1 align-[-3px]" />
            {FEEDBACK_LABELS[item]}
          </button>
        ))}
      </div>

      <div className="mt-4">
        <TextArea
          value={text}
          onChange={(event) => setText(event.target.value.slice(0, MAX_LEN))}
          placeholder={FEEDBACK_PLACEHOLDERS[kind]}
          rows={5}
          autoFocus
        />
      </div>

      <p className="text-text-muted mt-3 px-1 text-caption leading-snug">
        Мы увидим текст, ваше имя и экран, с которого вы пишете. Данные семьи —
        фото, заметки и суммы — не прикладываются.
      </p>
    </Sheet>
  )
}
